import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { api, type BuildRow, type SessionRow, type PodStatusEvent } from '../lib/api.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { NewMigrationForm } from '../components/NewMigrationForm.js';

type BuildStatus = 'known' | 'unreachable' | 'unknown';

const statusColor: Record<BuildStatus, string> = {
  known: 'var(--accent-green)',
  unreachable: 'var(--accent-red)',
  unknown: 'var(--text-dimmed)',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function sessionStatusColor(status: string): string {
  switch (status) {
    case 'done': case 'completed': return 'var(--accent-green)';
    case 'running': return 'var(--accent-blue)';
    case 'failed': return 'var(--accent-red)';
    default: return 'var(--text-dimmed)';
  }
}

function MiniStat({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="rounded-xl p-3 flex flex-col gap-1" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}>
      <div className="text-xl font-bold" style={{ color: color ?? 'var(--text-primary)' }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--text-dimmed)' }}>{label}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 shrink-0 text-xs text-text-dimmed">{label}</div>
      <div className="text-xs text-text-secondary">{value}</div>
    </div>
  );
}

const SENSITIVE_PATTERN = /password|token|key/i;

function SensitiveValue({ value }: { value: string }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span className="inline-flex items-center gap-2">
      <code className="font-mono text-xs">{revealed ? value : '••••••••'}</code>
      <button
        onClick={() => setRevealed(r => !r)}
        className="text-[10px] text-text-dimmed hover:text-text-base transition-colors"
      >
        {revealed ? 'hide' : 'reveal'}
      </button>
    </span>
  );
}

function ConfigPanel({ config }: { config: Record<string, unknown> | null }) {
  if (!config) return null;
  const inner = (config.config as Record<string, unknown>) ?? config;
  return (
    <div className="rounded-2xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(20px)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-text-dimmed mb-3">Clinic configuration</div>
      <div className="space-y-2">
        {Object.entries(inner).map(([k, v]) => (
          <Row
            key={k}
            label={k}
            value={
              SENSITIVE_PATTERN.test(k)
                ? <SensitiveValue value={String(v)} />
                : <code className="font-mono text-xs">{String(v)}</code>
            }
          />
        ))}
      </div>
    </div>
  );
}

function PodStatusPanel({ buildId }: { buildId: string }) {
  const [status, setStatus] = useState<PodStatusEvent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const es = new EventSource(`/api/builds/${buildId}/status/stream`);
    es.onmessage = (e: MessageEvent) => {
      const data = JSON.parse(e.data as string) as PodStatusEvent;
      if (data.error) {
        setError(data.error);
      } else {
        setStatus(data);
        setError(null);
      }
    };
    es.onerror = () => setError('Connection lost — retrying…');
    return () => es.close();
  }, [buildId]);

  return (
    <div className="rounded-2xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(20px)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-text-dimmed mb-3">Pod status</div>
      {error && <div className="text-xs mb-3" style={{ color: 'var(--accent-red)' }}>⚠ {error}</div>}
      {!status && !error && <div className="text-xs text-text-dimmed">Connecting…</div>}
      {status && (
        <div className="space-y-4">
          {status.pod ? (
            <div className="space-y-1.5">
              <Row label="Pod" value={<code className="font-mono text-xs">{status.pod.name}</code>} />
              <Row label="Phase" value={status.pod.phase} />
              <Row label="Started" value={timeAgo(status.pod.startedAt)} />
            </div>
          ) : (
            <div className="text-xs text-text-dimmed">No pod found</div>
          )}
          {status.containers.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-dimmed mb-2">Containers</div>
              <div className="space-y-1.5">
                {status.containers.map((c) => (
                  <div key={c.name} className="flex items-center gap-3 text-xs">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: c.ready ? 'var(--accent-green)' : 'var(--accent-red)' }} />
                    <code className="font-mono text-text-primary w-40 truncate">{c.name}</code>
                    <span className="text-text-dimmed">{c.restartCount > 0 ? `↺ ${c.restartCount}` : ''}</span>
                    <code className="font-mono text-[10px] text-text-dimmed truncate flex-1">{c.image.split('/').pop()}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
          {status.events.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-dimmed mb-2">Recent events</div>
              <div className="space-y-1">
                {status.events.map((ev, i) => (
                  <div key={i} className="text-xs flex gap-2">
                    <span style={{ color: ev.type === 'Warning' ? 'var(--accent-red)' : 'var(--text-dimmed)' }} className="w-20 shrink-0">{ev.reason}</span>
                    <span className="text-text-secondary truncate">{ev.message}</span>
                    <span className="text-text-dimmed shrink-0">{timeAgo(ev.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LogTailPanel({ buildId }: { buildId: string }) {
  const [active, setActive] = useState(false);
  const [lines, setLines] = useState<string[]>([]);
  const [interrupted, setInterrupted] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const start = () => {
    setLines([]);
    setInterrupted(false);
    setActive(true);
    const es = new EventSource(`/api/builds/${buildId}/logs/stream`);
    esRef.current = es;
    es.onmessage = (e: MessageEvent) => {
      const data = JSON.parse(e.data as string) as { line?: string; done?: boolean; error?: string };
      if (data.line) {
        setLines((prev) => [...prev.slice(-500), data.line!]);
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
      if (data.done || data.error) {
        setInterrupted(true);
        es.close();
        setActive(false);
      }
    };
    es.onerror = () => {
      setInterrupted(true);
      es.close();
      setActive(false);
    };
  };

  const stop = () => {
    esRef.current?.close();
    setActive(false);
  };

  return (
    <div className="rounded-2xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(20px)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-text-dimmed">Live logs</div>
        <div className="flex gap-2">
          {interrupted && (
            <button type="button" onClick={start} className="text-xs px-3 py-1 rounded-lg" style={{ background: 'var(--bg-tertiary)', color: 'var(--accent-yellow)' }}>
              Reconnect
            </button>
          )}
          {active ? (
            <button type="button" onClick={stop} className="text-xs px-3 py-1 rounded-lg" style={{ background: 'var(--bg-tertiary)', color: 'var(--accent-red)' }}>
              Stop
            </button>
          ) : (
            <button type="button" onClick={start} className="text-xs px-3 py-1 rounded-lg text-white" style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}>
              Live Logs
            </button>
          )}
        </div>
      </div>
      {interrupted && <div className="text-xs mb-2" style={{ color: 'var(--accent-yellow)' }}>⚠ Stream interrupted</div>}
      {lines.length === 0 && !active && !interrupted && (
        <div className="text-xs text-text-dimmed">Click "Live Logs" to start tailing.</div>
      )}
      {lines.length > 0 && (
        <div className="font-mono text-[11px] text-text-secondary overflow-y-auto max-h-64 space-y-0.5" style={{ background: 'var(--bg-primary)', borderRadius: '8px', padding: '12px' }}>
          {lines.map((line, i) => (
            <div key={i} className="whitespace-pre-wrap break-all">{line}</div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
}

export function BuildDetailPage({ buildId }: { buildId: string }) {
  const qc = useQueryClient();
  const [showImportModal, setShowImportModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['builds', buildId],
    queryFn: () => api.getBuild(buildId),
    refetchInterval: 60_000,
  });

  usePageTitle(data?.build.displayName ?? 'Build');

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><div className="text-sm text-text-dimmed">Loading…</div></div>;
  }
  if (!data) {
    return <div className="flex items-center justify-center h-full"><div className="text-sm text-text-dimmed">Build not found.</div></div>;
  }

  const { build, sessions, sessionStats } = data;
  const status = build.status as BuildStatus;
  const buildRow = build as BuildRow;

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center gap-3">
          <Link to="/builds" className="text-xs text-text-dimmed hover:text-text-secondary transition-colors">← Builds</Link>
          <span className="text-text-dimmed text-xs">/</span>
          <span className="text-sm font-semibold text-text-primary">{build.displayName}</span>
          <span
            className="flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
            style={{ background: `${statusColor[status]}22`, color: statusColor[status], border: `1px solid ${statusColor[status]}44` }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: statusColor[status] }} />
            {build.status}
          </span>
          {buildRow.region && (
            <span className="font-mono text-[10px] rounded-full px-2 py-0.5" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dimmed)' }}>
              {buildRow.region}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/storage"
            search={{ bucket: 'cerbo-import-data', prefix: `${build.namespace}/` }}
            className="rounded-xl px-4 py-2 text-sm font-semibold transition-opacity duration-150"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}
            title="Browse this build's S3 import data"
          >
            ☁ Storage
          </Link>
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', boxShadow: '0 2px 12px rgba(13,207,184,0.3)' }}
          >
            + New Import
          </button>
        </div>
      </header>

      <section className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-7 rounded-2xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(20px)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-text-dimmed mb-3">Build identity</div>
            <div className="space-y-2.5">
              <Row label="Namespace" value={<code className="font-mono text-xs text-text-primary">{build.namespace}</code>} />
              <Row label="Region" value={<code className="font-mono text-xs text-text-secondary">{buildRow.region ?? '—'}</code>} />
              <Row label="Last seen" value={timeAgo(build.lastSeenAt)} />
            </div>
          </div>
          <div className="col-span-5 grid grid-cols-2 gap-3 content-start">
            <MiniStat label="Total sessions" value={sessionStats.total} />
            <MiniStat label="Completed" value={sessionStats.completed} color="var(--accent-green)" />
            <MiniStat label="Running" value={sessionStats.running} color="var(--accent-blue)" />
            <MiniStat label="Failed" value={sessionStats.failed} color={sessionStats.failed > 0 ? 'var(--accent-red)' : undefined} />
          </div>
        </div>

        {buildRow.config && <ConfigPanel config={buildRow.config} />}

        <PodStatusPanel buildId={buildId} />

        <LogTailPanel buildId={buildId} />

        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary mb-3">Import sessions</div>
          <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            {sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <div className="text-2xl opacity-30">📋</div>
                <div className="text-sm font-semibold text-text-secondary">No import sessions yet</div>
                {status === 'known' && (
                  <button type="button" onClick={() => setShowImportModal(true)} className="mt-2 rounded-lg px-4 py-2 text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}>
                    + Start first import
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider">Title</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider">Started</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider">Finished</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider">Sources</th>
                  </tr>
                </thead>
                <tbody>
                  {(sessions as SessionRow[]).map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0 cursor-pointer hover:bg-white/5" onClick={() => { window.location.href = `/sessions/${s.id}`; }}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-text-primary">{s.title ?? `Session ${s.id.slice(0, 8)}`}</div>
                        <div className="font-mono text-[10px] text-text-dimmed">{s.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" style={{ background: `${sessionStatusColor(s.status)}22`, color: sessionStatusColor(s.status) }}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-text-dimmed">{timeAgo(s.startedAt)}</td>
                      <td className="px-5 py-3 font-mono text-xs text-text-dimmed">{timeAgo(s.finishedAt)}</td>
                      <td className="px-5 py-3 text-xs text-text-secondary">{s.sources.map((src) => src.kind).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-[860px] rounded-2xl p-7" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 3px 40px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-semibold text-text-primary">New Import Session</div>
              <button type="button" onClick={() => setShowImportModal(false)} className="text-sm text-text-secondary hover:text-text-primary">✕</button>
            </div>
            <NewMigrationForm
              initialBuildId={buildId}
              onCreated={(id) => {
                setShowImportModal(false);
                void qc.invalidateQueries({ queryKey: ['builds', buildId] });
                window.location.href = `/sessions/${id}`;
              }}
              onCancel={() => setShowImportModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
